import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Offcanvas, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.onmouseenter = Swal.stopTimer;
    toast.onmouseleave = Swal.resumeTimer;
  }
});

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const LicenseKeyList = () => {
  const [keys, setKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/v360-license-keys`);
      setKeys(response.data);
    } catch (error) {
      console.error('Error fetching license keys', error);
      Toast.fire({ icon: 'error', title: 'Failed to load license keys' });
    }
  };

  const handleShowModal = (record = null) => {
    setCurrentRecord(record);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setCurrentRecord(null);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    data.status = formData.get('status') === 'on';

    try {
      if (currentRecord && currentRecord.id) {
        await axios.patch(`${backendUrl}/api/v360-license-keys/${currentRecord.id}`, data);
        Toast.fire({ icon: 'success', title: 'License key updated successfully' });
      } else {
        await axios.post(`${backendUrl}/api/v360-license-keys`, data);
        Toast.fire({ icon: 'success', title: 'License key created successfully' });
      }
      handleCloseModal();
      fetchKeys();
    } catch (error) {
      console.error('Error saving record', error);
      Toast.fire({ icon: 'error', title: 'Failed to save license key' });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${backendUrl}/api/v360-license-keys/${id}`);
          Swal.fire('Deleted!', 'Record has been deleted.', 'success');
          fetchKeys();
        } catch (error) {
          console.error('Error deleting record', error);
          Swal.fire('Error!', 'Failed to delete record.', 'error');
        }
      }
    });
  };

  const handleToggleStatus = async (record) => {
    if (record.status) return; // already active

    try {
      await axios.patch(`${backendUrl}/api/v360-license-keys/${record.id}`, { status: true });
      Toast.fire({ icon: 'success', title: 'License key set as active' });
      fetchKeys();
    } catch (error) {
      console.error('Error toggling status', error);
      Toast.fire({ icon: 'error', title: 'Failed to set active status' });
    }
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <div className="row w-100">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18">License Keys Management</h4>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-end mb-3">
                    <button
                      type="button"
                      className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                      onClick={() => handleShowModal()}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Add License Key"
                    >
                      <i className="mdi mdi-plus"></i>
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table align-middle table-nowrap table-check table-list">
                      <thead className="table-sales-gradient">
                        <tr>
                          <th className="align-middle text-center">ID</th>
                          <th className="align-middle text-center">Name</th>
                          <th className="align-middle text-center">Status</th>
                          <th className="align-middle text-center">Created At</th>
                          <th className="align-middle text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((key) => (
                          <tr key={key.id}>
                            <td className="text-center">{key.id}</td>
                            <td className="text-center">{key.name}</td>
                            <td className="text-center">
                              {key.status ? (
                                <span className="badge bg-success">Active</span>
                              ) : (
                                <span className="badge bg-secondary">Inactive</span>
                              )}
                            </td>
                            <td className="text-center">{new Date(key.created_at).toLocaleDateString()}</td>
                            <td className="text-center">
                              <div className="d-flex gap-2 justify-content-center">
                                {!key.status && (
                                  <button
                                    title="Set as Active"
                                    className="btn btn-sm btn-soft-success"
                                    onClick={() => handleToggleStatus(key)}
                                  >
                                    <i className="mdi mdi-check"></i>
                                  </button>
                                )}
                                <button
                                  title="Edit"
                                  className="btn btn-sm btn-soft-info"
                                  onClick={() => handleShowModal(key)}
                                >
                                  <i className="mdi mdi-pencil font-size-16"></i>
                                </button>
                                <button
                                  title="Delete"
                                  className="btn btn-sm btn-soft-danger"
                                  onClick={() => handleDelete(key.id)}
                                >
                                  <i className="mdi mdi-delete font-size-16"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {keys.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center">No records found.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Offcanvas show={showModal} onHide={handleCloseModal} placement="end" style={{ width: '500px' }}>
          <Offcanvas.Header closeButton>
            <Offcanvas.Title className="fw-bold text-primary">{currentRecord ? 'Edit License Key' : 'Add License Key'}</Offcanvas.Title>
          </Offcanvas.Header>
          <hr style={{ margin: 0 }} />
          <Offcanvas.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">Name</Form.Label>
                <Form.Control
                  className="form-feild"
                  type="text"
                  name="name"
                  defaultValue={currentRecord?.name || ''}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="form-label form-font">RSA Public Key (PEM format)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  name="public_key"
                  defaultValue={currentRecord?.public_key || ''}
                  required
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="status-switch"
                  label={<span className="form-label form-font ms-2">Set as Active Key</span>}
                  name="status"
                  defaultChecked={currentRecord?.status || false}
                />
              </Form.Group>
              <div className="d-flex justify-content-end mt-4 mb-4">
                <Button variant="secondary" onClick={handleCloseModal} className="me-2">
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Save
                </Button>
              </div>
            </Form>
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </div>
  );
};

export default LicenseKeyList;
